import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { LandingPageSection } from "@/models/storeModel";
import { ImageUploader } from "./ImageUploader";

interface SectionEditorProps {
  section: LandingPageSection;
  onUpdate: (content: any) => void;
  themeColors: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

export function SectionEditor({ section, onUpdate, themeColors }: SectionEditorProps) {
  const [content, setContent] = useState(section.content);

  const handleUpdate = (newContent: any) => {
    setContent(newContent);
    onUpdate(newContent);
  };

  const renderEditor = () => {
    switch (section.type) {
      case "logo":
        return (
          <div className="space-y-4">
            <ImageUploader
              currentUrl={content.logoUrl || ""}
              onImageUpload={(url) => handleUpdate({ ...content, logoUrl: url })}
              section="logo"
              label="Logo Image"
              placeholder="Upload logo or enter URL"
            />
            <div>
              <Label>Business Name</Label>
              <Input
                value={content.businessName || ""}
                onChange={(e) => handleUpdate({ ...content, businessName: e.target.value })}
                placeholder="Your business name"
              />
            </div>
          </div>
        );

      case "hero":
        return (
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={content.title || ""}
                onChange={(e) => handleUpdate({ ...content, title: e.target.value })}
                placeholder="Main headline"
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Textarea
                value={content.subtitle || ""}
                onChange={(e) => handleUpdate({ ...content, subtitle: e.target.value })}
                placeholder="Supporting text"
              />
            </div>
            <ImageUploader
              currentUrl={content.backgroundImage || ""}
              onImageUpload={(url) => handleUpdate({ ...content, backgroundImage: url })}
              section="hero"
              label="Background Image"
              placeholder="Upload background image or enter URL"
            />
            <div>
              <Label>Call-to-Action Button Text</Label>
              <Input
                value={content.ctaText || ""}
                onChange={(e) => handleUpdate({ ...content, ctaText: e.target.value })}
                placeholder="Get Started"
              />
            </div>
            <div>
              <Label>CTA Link</Label>
              <Input
                value={content.ctaLink || ""}
                onChange={(e) => handleUpdate({ ...content, ctaLink: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
        );

      case "about":
        return (
          <div className="space-y-4">
            <div>
              <Label>Section Title</Label>
              <Input
                value={content.title || ""}
                onChange={(e) => handleUpdate({ ...content, title: e.target.value })}
                placeholder="About Us"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={content.description || ""}
                onChange={(e) => handleUpdate({ ...content, description: e.target.value })}
                placeholder="Tell your story..."
                rows={6}
              />
            </div>
            <ImageUploader
              currentUrl={content.image || ""}
              onImageUpload={(url) => handleUpdate({ ...content, image: url })}
              section="about"
              label="About Section Image"
              placeholder="Upload image or enter URL"
            />
          </div>
        );

      case "menu":
        return (
          <div className="space-y-4">
            <div>
              <Label>Menu Title</Label>
              <Input
                value={content.title || ""}
                onChange={(e) => handleUpdate({ ...content, title: e.target.value })}
                placeholder="Our Menu"
              />
            </div>
            <div>
              <Label>Menu Items</Label>
              {content.items?.map((item: any, index: number) => (
                <Card key={index} className="mt-4">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Item {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newItems = content.items.filter((_: any, i: number) => i !== index);
                            handleUpdate({ ...content, items: newItems });
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Name</Label>
                          <Input
                            value={item.name}
                            onChange={(e) => {
                              const newItems = [...content.items];
                              newItems[index].name = e.target.value;
                              handleUpdate({ ...content, items: newItems });
                            }}
                            placeholder="Dish name"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Price</Label>
                          <Input
                            value={item.price}
                            onChange={(e) => {
                              const newItems = [...content.items];
                              newItems[index].price = e.target.value;
                              handleUpdate({ ...content, items: newItems });
                            }}
                            placeholder="$24.99"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Description</Label>
                        <Textarea
                          value={item.description}
                          onChange={(e) => {
                            const newItems = [...content.items];
                            newItems[index].description = e.target.value;
                            handleUpdate({ ...content, items: newItems });
                          }}
                          placeholder="Dish description"
                          rows={2}
                        />
                      </div>
                      <ImageUploader
                        currentUrl={item.image || ""}
                        onImageUpload={(url) => {
                          const newItems = [...content.items];
                          newItems[index].image = url;
                          handleUpdate({ ...content, items: newItems });
                        }}
                        section={`menu-item-${index}`}
                        label="Item Image"
                        placeholder="Upload item image or enter URL"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button
                variant="outline"
                onClick={() => {
                  const newItems = [...(content.items || []), { 
                    name: "New Dish", 
                    description: "Delicious new item", 
                    price: "$19.99",
                    image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=300&fit=crop"
                  }];
                  handleUpdate({ ...content, items: newItems });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Menu Item
              </Button>
            </div>
          </div>
        );

      case "image":
        return (
          <div className="space-y-4">
            <ImageUploader
              currentUrl={content.url || ""}
              onImageUpload={(url) => handleUpdate({ ...content, url: url })}
              section="image"
              label="Image"
              placeholder="Upload image or enter URL"
            />
            <div>
              <Label>Alt Text</Label>
              <Input
                value={content.alt || ""}
                onChange={(e) => handleUpdate({ ...content, alt: e.target.value })}
                placeholder="Image description"
              />
            </div>
            <div>
              <Label>Caption (optional)</Label>
              <Input
                value={content.caption || ""}
                onChange={(e) => handleUpdate({ ...content, caption: e.target.value })}
                placeholder="Image caption"
              />
            </div>
          </div>
        );

      case "reservations":
        return (
          <div className="space-y-4">
            <div>
              <Label>Section Title</Label>
              <Input
                value={content.title || ""}
                onChange={(e) => handleUpdate({ ...content, title: e.target.value })}
                placeholder="Make a Reservation"
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input
                value={content.subtitle || ""}
                onChange={(e) => handleUpdate({ ...content, subtitle: e.target.value })}
                placeholder="Book your table now"
              />
            </div>
            <div>
              <Label>Button Text</Label>
              <Input
                value={content.buttonText || ""}
                onChange={(e) => handleUpdate({ ...content, buttonText: e.target.value })}
                placeholder="Reserve Now"
              />
            </div>
            <div>
              <Label>Reservation URL</Label>
              <Input
                value={content.reservationUrl || ""}
                onChange={(e) => handleUpdate({ ...content, reservationUrl: e.target.value })}
                placeholder="https://yourstore.com/booking/..."
              />
            </div>
          </div>
        );

      case "offers":
        return (
          <div className="space-y-4">
            <div>
              <Label>Section Title</Label>
              <Input
                value={content.title || ""}
                onChange={(e) => handleUpdate({ ...content, title: e.target.value })}
                placeholder="Special Offers"
              />
            </div>
            <div>
              <Label>Offers (JSON format)</Label>
              <Textarea
                value={JSON.stringify(content.offers || [], null, 2)}
                onChange={(e) => {
                  try {
                    const offers = JSON.parse(e.target.value);
                    handleUpdate({ ...content, offers });
                  } catch (err) {
                    // Invalid JSON, don't update
                  }
                }}
                placeholder='[{"title": "Offer 1", "description": "Description", "image": "url"}]'
                rows={8}
              />
            </div>
          </div>
        );

      case "footer":
        return (
          <div className="space-y-4">
            <div>
              <Label>Contact Email</Label>
              <Input
                value={content.contactInfo?.email || ""}
                onChange={(e) => handleUpdate({ 
                  ...content, 
                  contactInfo: { 
                    ...content.contactInfo, 
                    email: e.target.value 
                  } 
                })}
                placeholder="contact@yourcompany.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={content.contactInfo?.phone || ""}
                onChange={(e) => handleUpdate({ 
                  ...content, 
                  contactInfo: { 
                    ...content.contactInfo, 
                    phone: e.target.value 
                  } 
                })}
                placeholder="+1 234 567 890"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Textarea
                value={content.contactInfo?.address || ""}
                onChange={(e) => handleUpdate({ 
                  ...content, 
                  contactInfo: { 
                    ...content.contactInfo, 
                    address: e.target.value 
                  } 
                })}
                placeholder="Your business address"
              />
            </div>
            
            <div className="space-y-3">
              <Label className="text-sm font-medium">Social Media Links</Label>
              
              <div>
                <Label className="text-xs">Facebook URL</Label>
                <Input
                  value={content.socialLinks?.facebook || ""}
                  onChange={(e) => handleUpdate({ 
                    ...content, 
                    socialLinks: { 
                      ...content.socialLinks, 
                      facebook: e.target.value 
                    } 
                  })}
                  placeholder="https://facebook.com/yourpage"
                />
              </div>
              
              <div>
                <Label className="text-xs">Instagram URL</Label>
                <Input
                  value={content.socialLinks?.instagram || ""}
                  onChange={(e) => handleUpdate({ 
                    ...content, 
                    socialLinks: { 
                      ...content.socialLinks, 
                      instagram: e.target.value 
                    } 
                  })}
                  placeholder="https://instagram.com/yourpage"
                />
              </div>
              
              <div>
                <Label className="text-xs">Twitter URL</Label>
                <Input
                  value={content.socialLinks?.twitter || ""}
                  onChange={(e) => handleUpdate({ 
                    ...content, 
                    socialLinks: { 
                      ...content.socialLinks, 
                      twitter: e.target.value 
                    } 
                  })}
                  placeholder="https://twitter.com/yourpage"
                />
              </div>
              
              <div>
                <Label className="text-xs">LinkedIn URL</Label>
                <Input
                  value={content.socialLinks?.linkedin || ""}
                  onChange={(e) => handleUpdate({ 
                    ...content, 
                    socialLinks: { 
                      ...content.socialLinks, 
                      linkedin: e.target.value 
                    } 
                  })}
                  placeholder="https://linkedin.com/company/yourpage"
                />
              </div>
              
              <div>
                <Label className="text-xs">YouTube URL</Label>
                <Input
                  value={content.socialLinks?.youtube || ""}
                  onChange={(e) => handleUpdate({ 
                    ...content, 
                    socialLinks: { 
                      ...content.socialLinks, 
                      youtube: e.target.value 
                    } 
                  })}
                  placeholder="https://youtube.com/channel/yourpage"
                />
              </div>
            </div>
          </div>
        );

      case "text":
        return (
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={content.title || ""}
                onChange={(e) => handleUpdate({ ...content, title: e.target.value })}
                placeholder="Section title"
              />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea
                value={content.content || ""}
                onChange={(e) => handleUpdate({ ...content, content: e.target.value })}
                placeholder="Your text content..."
                rows={4}
              />
            </div>
            <div>
              <Label>Text Alignment</Label>
              <Select 
                value={content.alignment || "left"} 
                onValueChange={(value) => handleUpdate({ ...content, alignment: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case "button":
        return (
          <div className="space-y-4">
            <div>
              <Label>Button Text</Label>
              <Input
                value={content.text || ""}
                onChange={(e) => handleUpdate({ ...content, text: e.target.value })}
                placeholder="Button text"
              />
            </div>
            <div>
              <Label>Link URL</Label>
              <Input
                value={content.link || ""}
                onChange={(e) => handleUpdate({ ...content, link: e.target.value })}
                placeholder="https://..."
                type="url"
              />
            </div>
            <div>
              <Label>Button Style</Label>
              <Select 
                value={content.style || "primary"} 
                onValueChange={(value) => handleUpdate({ ...content, style: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Primary</SelectItem>
                  <SelectItem value="secondary">Secondary</SelectItem>
                  <SelectItem value="outline">Outline</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      default:
        return <div>Select a section to edit</div>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="capitalize">{section.type} Settings</CardTitle>
      </CardHeader>
      <CardContent>
        {renderEditor()}
      </CardContent>
    </Card>
  );
}
